import { useState, useMemo, useRef, useEffect, useCallback, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useEventListener, useOutsideClick, useThrottle } from '@shared/hooks';
import { cn, PaletteUtils } from '@shared/utils';
import { ErrorMessage, Icon, Text } from '@shared/widgets';
import ChevronIcon from '@assets/icons/chevron.svg';
import CrossIcon from '@assets/icons/cross.svg';

export const MultiselectVariant = {
  Count: 'count',
  Chips: 'chips',
} as const;
export type MultiselectVariant = (typeof MultiselectVariant)[keyof typeof MultiselectVariant];

interface IMultiselectProps<T> {
  className?: string;
  wrapperClassName?: string;
  buttonClassName?: string;
  iconClassName?: string;
  label?: ReactNode;
  variant?: MultiselectVariant;
  isLoading?: boolean;
  nullValue?: { isEnable: boolean; text?: string };
  items: { label: string; value: T }[] | string[];
  error?: ReactNode;
  required?: boolean;
  disabled?: boolean;
  value?: T[];
  onChange: (value?: T[]) => void;
}

const isMultiselectValueEqual = <T,>(a: T, b: T) => {
  if (Object.is(a, b)) return true;
  if (typeof a === 'object' && a !== null && typeof b === 'object' && b !== null) return JSON.stringify(a) === JSON.stringify(b);
  return false;
};

export function Multiselect<T>(props: IMultiselectProps<T>) {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [menuStyles, setMenuStyles] = useState<React.CSSProperties>({});
  const selectRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLDivElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);

  const { t } = useTranslation();

  const updateMenuPosition = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    setMenuStyles({
      width: rect.width,
      position: 'absolute',
      top: rect.bottom + window.scrollY,
      left: rect.left + window.scrollX,
    });
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    updateMenuPosition();
  }, [isOpen, props.value, updateMenuPosition]);

  const throttledUpdateMenuPosition = useThrottle(updateMenuPosition, 10);
  useEventListener('scroll', throttledUpdateMenuPosition, undefined, true);
  useEventListener('resize', throttledUpdateMenuPosition);
  useOutsideClick([selectRef, menuRef], () => setIsOpen(false));

  const isChips = (props.variant ?? MultiselectVariant.Count) === MultiselectVariant.Chips;
  const defaultNullValueText = t('placeholders.all');
  const nullText = `${props.nullValue?.text || defaultNullValueText}${props.required ? ' *' : ''}`;
  const selectedItems = useMemo(() => {
    if (!Array.isArray(props.value) || props.value.length === 0) return [];
    return props.value.map((selected) => {
      const match = props.items.find((item) => isMultiselectValueEqual((item as { value?: T }).value ?? (item as T), selected));
      const label = match ? ((match as { label?: string }).label ?? String(match)) : String(selected);
      const value = match ? ((match as { value?: T }).value ?? (match as T)) : selected;
      return { label, value };
    });
  }, [props.value, props.items]);
  const currentLabel = useMemo(() => {
    if (props.isLoading) return t('select.loading');
    if (props.items.length === 0 && props.value?.length === 0) return undefined;
    if (!isChips && selectedItems.length > 0) return `Selected: ${selectedItems.length}`;
    return undefined;
  }, [props.value, props.items, props.isLoading, isChips, selectedItems.length]);

  const handleSelectChange = (selectedValue: T) => {
    const currentValue = Array.isArray(props.value) ? props.value : [];
    const isSelected = currentValue.some((v) => isMultiselectValueEqual(v, selectedValue));
    const newValue = isSelected ? currentValue.filter((v) => !isMultiselectValueEqual(v, selectedValue)) : [...currentValue, selectedValue];
    props.onChange(newValue);
  };

  return (
    <div ref={selectRef} className={cn('relative flex w-auto flex-col', props.disabled && 'opacity-60', props.className)}>
      {props.label && (
        <Text className='w-full text-left' tag='label' size='sm' color='secondary'>
          {props.label} {props.required ? ' *' : ''}
        </Text>
      )}
      <div
        ref={triggerRef}
        className={cn(
          'flex h-10 w-full cursor-pointer transition-all duration-200',
          PaletteUtils.isInsigniaPalette && [
            'border border-transparent border-b-light bg-ice dark:bg-soft-black',
            'hover:border hover:border-primary hover:bg-white',
          ],
          PaletteUtils.isVcfcPalette && [
            'h-[50px] rounded-[100px] border border-light/70 bg-white dark:border-transparent dark:bg-white/10',
            'hover:border-primary dark:hover:border-light/70',
          ],
          isChips && selectedItems.length > 0 && ['h-auto min-h-10', PaletteUtils.isVcfcPalette && 'min-h-[50px] rounded-[28px]'],
          props.wrapperClassName,
        )}
        onClick={() => {
          if (props.disabled || props.isLoading) return;
          setIsOpen(!isOpen);
        }}
      >
        <div className={cn('flex w-full items-center justify-between gap-2 px-2 py-2', PaletteUtils.isVcfcPalette && 'px-4')}>
          {isChips && selectedItems.length > 0 ? (
            <div className='flex min-w-0 flex-1 flex-wrap items-center gap-2'>
              {selectedItems.map((item) => (
                <button
                  key={typeof item.value === 'object' ? JSON.stringify(item.value) : String(item.value)}
                  className='flex items-center gap-1 rounded-lg bg-ghost-white px-2.5 py-1 text-sm font-medium text-soft-black dark:bg-white/10 dark:text-white'
                  type='button'
                  onClick={(e) => (e.stopPropagation(), handleSelectChange(item.value))}
                >
                  {item.label}
                  <Icon icon={<CrossIcon className='size-4' />} color='dark' />
                </button>
              ))}
            </div>
          ) : (
            <p
              className={cn(
                'w-full text-nowrap text-left font-light leading-normal',
                PaletteUtils.isVcfcPalette && 'text-dark-wet-asphalt dark:text-white/70',
                PaletteUtils.isInsigniaPalette && 'text-dark-wet-asphalt dark:text-gray-dark/70',
                props.value && 'text-soft-black dark:text-white',
              )}
            >
              {currentLabel ?? nullText}
            </p>
          )}
          <div
            className={cn(
              'flex items-center justify-center',
              PaletteUtils.isInsigniaPalette && 'relative left-[9px] h-10 w-10 shrink-0 bg-soft-black dark:bg-slate',
              props.buttonClassName,
            )}
          >
            <Icon
              icon={<ChevronIcon />}
              className={cn('-rotate-90 transition-transform duration-200', isOpen && 'rotate-90', props.iconClassName)}
              color={PaletteUtils.getColor('primary', PaletteUtils.isVcfcPalette ? 'dark' : 'white')}
            />
          </div>
        </div>
      </div>
      <ErrorMessage className='text-left' text={props.error} />
      {isOpen &&
        createPortal(
          <div
            ref={menuRef}
            className={cn(
              'absolute z-[99] flex max-h-40 w-full flex-col overflow-hidden border bg-white dark:bg-soft-black',
              PaletteUtils.isVcfcPalette && 'rounded-2xl border-light/70',
            )}
            style={menuStyles}
          >
            <div className={cn('h-full overflow-y-auto', PaletteUtils.isVcfcPalette && 'py-2')}>
              {props.nullValue?.isEnable && (
                <SelectItem
                  label={props.nullValue.text ?? defaultNullValueText}
                  isSelected={!props.value || (Array.isArray(props.value) && props.value.length === 0)}
                  onChange={() => {
                    props.onChange([]);
                    setIsOpen(false);
                  }}
                />
              )}
              {props.items.length === 0 ? (
                <div className='flex h-8 items-center justify-center bg-white dark:bg-soft-black'>
                  <p className='font-light text-soft-black opacity-60 dark:text-white'>{t('select.noOptions')}</p>
                </div>
              ) : (
                props.items.map((x, key) => {
                  const label = (x as any).label ?? x;
                  const value = (x as any).value ?? x;
                  const isSelected = Array.isArray(props.value) && props.value.some((v) => isMultiselectValueEqual(v, value));
                  return (
                    <SelectItem<T>
                      key={key}
                      value={value}
                      label={label}
                      isSelected={isSelected}
                      onChange={() => {
                        handleSelectChange(value);
                      }}
                    />
                  );
                })
              )}
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
}

interface ISelectItemProps<T> {
  value?: T;
  label: string;
  isSelected: boolean;
  onChange: () => void;
}

function SelectItem<T>(props: ISelectItemProps<T>) {
  return (
    <div
      onClick={props.onChange}
      className={cn(
        'z-10 flex cursor-pointer bg-white dark:bg-soft-black',
        PaletteUtils.isInsigniaPalette && ['hover:bg-gray-silk dark:hover:bg-olive-gray', props.isSelected && 'bg-gray-silk dark:bg-olive-gray'],
        PaletteUtils.isVcfcPalette && ['hover:bg-primary-lightest dark:hover:bg-slate', props.isSelected && 'bg-primary-lightest dark:bg-slate'],
      )}
    >
      <p
        className={cn(
          'overflow-hidden text-ellipsis px-2 py-2 text-center font-light leading-normal',
          PaletteUtils.isVcfcPalette && ['px-4 text-soft-black dark:text-white/50', props.isSelected && 'text-primary dark:text-white'],
          PaletteUtils.isInsigniaPalette && ['text-soft-black dark:text-white', props.isSelected && 'text-primary dark:text-white'],
        )}
      >
        {props.label}
      </p>
    </div>
  );
}
